import React, { useEffect, useState, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { AuthContext } from "../../auth/auth-context";

const createCheckoutSession = process.env.REACT_APP_BACKEND_URL + "/create-checkout-session";
const billingInformationURL = process.env.REACT_APP_BACKEND_URL + "/create-portal-session";

const features = ["Unlimited Locations", "Unlimited Menus", "Choose your theme", "Support in getting started", "30 day money back guarantee"];

// ! Display this by default

const ProductDisplay = () => {
  const { authState } = useContext(AuthContext);
  const isTrialActive = authState.isTrialActive;
  const trialDaysRemaining = authState.trialDaysRemaining;
  const isOnTrial = authState.isOnTrial;

  return (
    <div className='mt-14 bg-white flex items-center justify-center'>
      <Helmet>
        <title>Subscription Plans | tastebud.ee</title>
        <meta name='robots' content='noindex' />
        <meta name='description' content='Choose your tastebud.ee subscription plan. Monthly at £5/month or annual at £30/year. Both plans include unlimited locations, menus and QR codes.' />
        <link rel='canonical' href='https://tastebud.ee/account/subscription' />
      </Helmet>
      <div className='px-6 max-w-screen-md w-full'>
        <h1 className='text-xl font-bold mb-4'>Subscription Plans</h1>

        {isTrialActive && (
          <div className='mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800'>
            You are on a free trial with{" "}
            <strong>
              {trialDaysRemaining} day{trialDaysRemaining !== 1 ? "s" : ""} remaining
            </strong>
            . Your trial includes 1 business, 1 menu, and 1 location. Subscribe any time for unlimited access.
          </div>
        )}

        {isOnTrial && !isTrialActive && (
          <div className='mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800'>Your free trial has ended. Subscribe below to continue using tastebud.ee.</div>
        )}

        <p className='mb-6'>Choose the plan that works for you — all plans include every feature. Pay monthly for flexibility, or save 50% by paying annually.</p>

        <div className='p-2 md:p-0 grid grid-cols-1 md:grid-cols-2 gap-6 justify-items-center'>
          {/* Monthly */}
          <div className='bg-white border-gray-50 shadow-2xl shadow-gray-400 w-72'>
            <div className='border-b-gray-800 border-b p-6 bg-[#7BC043]'>
              <h2 className='text-lg font-bold'>Monthly</h2>
              <p className='text-sm'>Pay as you go.</p>
            </div>
            <div className='px-6 py-3'>
              <h3 className='text-xl font-light py-2'>
                <span className='text-3xl'>£5.00</span> / month
              </h3>
              <p className='uppercase text-sm text-gray-600 pt-4'>Includes:</p>
              <ul className='list-disc list-inside mt-2 ml-4 text-sm leading-6 text-gray-600'>
                {features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <form action={createCheckoutSession} method='POST'>
                <input type='hidden' name='lookup_key' value={process.env.REACT_APP_STRIPE_PRODUCT_MONTHLY} />
                <button id='checkout-and-portal-button' type='submit' className='button-full'>
                  Subscribe monthly
                </button>
              </form>
            </div>
          </div>

          {/* Annual */}
          <div className='bg-white border-gray-50 shadow-2xl shadow-gray-400 w-72'>
            <div className='border-b-gray-800 border-b p-6 bg-yellow-400 flex items-start justify-between'>
              <div>
                <h2 className='text-lg font-bold'>Annual</h2>
                <p className='text-sm'>Best value.</p>
              </div>
              <span className='bg-black text-white text-xs font-bold px-2 py-1 rounded'>Save 50%</span>
            </div>
            <div className='px-6 py-3'>
              <h3 className='text-xl font-light py-2'>
                <span className='text-3xl'>£30.00</span> / year
              </h3>
              <p className='uppercase text-sm text-gray-600 pt-4'>Includes:</p>
              <ul className='list-disc list-inside mt-2 ml-4 text-sm leading-6 text-gray-600'>
                {features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <form action={createCheckoutSession} method='POST'>
                <input type='hidden' name='lookup_key' value={process.env.REACT_APP_STRIPE_PRODUCT_ANNUAL} />
                <button id='checkout-and-portal-button-annual' type='submit' className='button-full'>
                  Subscribe annually
                </button>
              </form>
            </div>
          </div>
        </div>
        <p className='m-10 text-center'>Please get in touch if you would like to discuss custom packages and pricing.</p>
      </div>
    </div>
  );
};

// ! Display this upon success

const SuccessDisplay = ({ sessionId }) => {
  return (
    <div className='mt-14 bg-white flex items-center justify-center'>
      <Helmet>
        <title>Subscription successful | tastebud.ee</title>
        <meta name='robots' content='noindex' />
      </Helmet>
      <div className='px-6 max-w-screen-sm w-full pb-10'>
        <h1 className='text-xl font-bold mb-4'>Subscription</h1>
        <p className='mb-4'>Your subscription is active. You can now continue to your account, or manage billing details.</p>

        <div className='p-2 md:p-0 grid grid-cols-1 gap-6 mb-10'>
          <div className='bg-white border-gray-50 shadow-2xl shadow-gray-400 max-w-md mx-auto w-72'>
            <div className='border-b-gray-800 border-b p-6 bg-[#7BC043]'>
              <h2 className='text-lg font-bold'>Subscription successful</h2>
              <p className='text-sm w-full'>Thanks for subscribing to tastebud.ee.</p>
            </div>
            <div className='px-6 py-3'>
              <form action={billingInformationURL} method='POST'>
                <input type='hidden' id='session-id' name='session_id' value={sessionId} />
                <button id='checkout-and-portal-button' type='submit' className='button-full'>
                  Manage billing information
                </button>
              </form>

              <div className='mt-4 text-sm text-gray-700'>
                Start setting up your business, menus and locations in <Link to='/account/my-account'>My Account</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Message = ({ message }) => (
  <div className='mt-14 bg-white flex items-center justify-center'>
    <Helmet>
      <title>Subscription | tastebud.ee</title>
      <meta name='robots' content='noindex' />
    </Helmet>
    <div className='px-6 max-w-screen-sm w-full'>
      <h1 className='text-xl font-bold mb-4'>Subscription</h1>
      <div className='p-2 md:p-0'>
        <div className='bg-white border-gray-50 shadow-2xl shadow-gray-400 max-w-md mx-auto w-72'>
          <div className='border-b-gray-800 border-b p-6 bg-[#FFFEDA]'>
            <h2 className='text-lg font-bold'>Something changed</h2>
            <p className='text-sm w-full'>No problem — you can try again any time.</p>
          </div>
          <div className='px-6 py-3 text-sm text-gray-700'>
            <p>{message}</p>
            <Link to='/account/subscription' className='inline-block mt-4'>
              View subscription plans
            </Link>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function Subscription() {
  let [message, setMessage] = useState("");
  let [success, setSuccess] = useState(false);
  let [sessionId, setSessionId] = useState("");

  useEffect(() => {
    // Check to see if this is a redirect back from Checkout
    const query = new URLSearchParams(window.location.search);

    if (query.get("success")) {
      setSuccess(true);
      setSessionId(query.get("session_id"));
    }

    if (query.get("canceled")) {
      setSuccess(false);
      setMessage("Order canceled -- continue to shop around and checkout when you're ready.");
    }
  }, [sessionId]);

  if (!success && message === "") {
    return <ProductDisplay />;
  } else if (success && sessionId !== "") {
    return <SuccessDisplay sessionId={sessionId} />;
  } else {
    return <Message message={message} />;
  }
}
